/**
 * ATSScreen.jsx — Premium ATS Score Dashboard & Resume Validator
 * Real-time connection to modular scoring engine v3.0.0.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, ShieldCheck, RefreshCw, Sparkles, Clock, Coins
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useWorkspaceContext } from '../../pages/ChatPage/providers/WorkspaceProvider';
import ScoreRing from './ScoreRing';
import HistorySparkline from './HistorySparkline';
import ImprovementCard from './ImprovementCard';

export default function ATSScreen() {
  const { resumeId } = useParams();
  const { setActiveTab } = useWorkspaceContext();
  const {
    authToken,
    resumeData,
    setResumeData,
    setEditMode,
    credits,
    setCredits,
    setToast
  } = useAppContext();

  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState('');

  const debounceTimerRef = useRef(null);

  // Fetch score progression history
  const fetchHistory = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch('/api/ats/v2/history', {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.warn('Failed to fetch scoring history:', err);
    }
  }, [authToken]);

  // Fetch fast evaluation report
  const fetchReport = useCallback(async (isSilent = false) => {
    if (!authToken || !resumeData) return;
    if (!isSilent) setLoading(true);
    setError('');
    const docId = resumeId || 'master';
    const docType = (resumeId && resumeId !== 'master') ? 'variant' : 'master';
    try {
      const res = await fetch('/api/intelligence/evaluate_fast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          domain_key: 'general',
          active_document: { id: docId, type: docType }
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to fetch ATS report.');
      }
      const data = await res.json();
      setReport(data);
      fetchHistory();
    } catch (err) {
      setError(err.message || 'Failed to compute score.');
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [authToken, resumeData, resumeId, fetchHistory]);

  // Run AI Semantic evaluation
  const handleRunAICheck = async () => {
    if (!authToken || aiLoading) return;
    setAiLoading(true);
    setError('');
    const docId = resumeId || 'master';
    const docType = (resumeId && resumeId !== 'master') ? 'variant' : 'master';
    try {
      const res = await fetch('/api/intelligence/evaluate_ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          domain_key: 'general',
          active_document: { id: docId, type: docType }
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to trigger AI check.');
      }
      const data = await res.json();
      setReport(data);
      fetchHistory();
    } catch (err) {
      setError(err.message || 'AI check failed.');
    } finally {
      setAiLoading(false);
    }
  };

  const resumeStr = JSON.stringify(resumeData);

  // Debounced live rescoring triggered when resumeData changes
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      fetchReport(true);
    }, 1000);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [resumeStr, fetchReport]);

  const handleFixWithAI = async (issue) => {
    if (!issue || !authToken) return;
    setAiLoading(true);
    setError('');
    try {
      const res = await fetch('/api/intelligence/fix_with_ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          issue_id: issue.id,
          location: issue.location,
          current_text: issue.current_text || '',
          domain_key: 'general'
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to apply AI fix.');
      }
      const data = await res.json();
      if (data.ok) {
        setResumeData(data.resume_data);
        setReport(data.ats_report);
        if (data.credits_remaining !== undefined && data.credits_remaining !== null) {
          setCredits(data.credits_remaining);
        }
        setToast?.({ message: 'AI applied recommendation successfully! Resume updated.', type: 'success' });
        fetchHistory();
      }
    } catch (err) {
      setError(err.message || 'AI fix failed.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleEditManually = (issue) => {
    setEditMode(true);
    setActiveTab('editor');
    setTimeout(() => {
      const fieldId = issue.location || issue.section;
      const element = document.getElementById(fieldId) || document.getElementsByName(fieldId)[0];
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  };

  if (loading && !report) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 text-slate-400 h-full">
        <RefreshCw className="w-8 h-8 animate-spin mb-4 text-sky-500" />
        <p className="text-sm font-medium animate-pulse">Running Resume Intelligence Engine scans...</p>
      </div>
    );
  }

  // Filter and sort issues into high-value Top Improvements
  const topImprovements = report?.issues
    ? [...report.issues]
        .filter(i => (i.potential_score_gain || 0) > 0)
        .sort((a, b) => (b.potential_score_gain || 0) - (a.potential_score_gain || 0))
    : [];

  const otherIssues = report?.issues
    ? [...report.issues].filter(i => !(i.potential_score_gain > 0))
    : [];

  return (
    <div className="flex-1 h-full overflow-y-auto px-4 pb-20 pt-[76px] md:px-8 md:pb-8 md:pt-[84px] space-y-8 select-none" style={{ background: 'var(--surface-0)' }}>
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#FCA311]/5 blur-[120px] pointer-events-none" />

      {/* Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-6 relative z-10" style={{ borderColor: 'var(--divider)' }}>
        <div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <ShieldCheck className="w-6 h-6 text-emerald-400" />
            Resume Intelligence Scorecard
          </h1>
          <p className="text-xs md:text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Real-time heuristic evaluation checks and diagnostics.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          {credits !== null && credits !== undefined && (
            <div className="flex items-center justify-center gap-1.5 px-2.5 py-2 sm:py-1 rounded-md text-[11px] font-semibold" style={{ background: 'var(--surface-1)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}>
              <Coins className="w-3 h-3 text-amber-500" />
              <span>{credits} Credits</span>
            </div>
          )}
          <button 
            onClick={() => fetchReport(false)} 
            disabled={loading}
            className="flex items-center justify-center gap-1.5 px-2.5 py-2 sm:py-1 rounded-md text-[11px] font-semibold active:scale-95 transition-all"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            <span>Fast Scan (FREE)</span>
          </button>
          <button 
            onClick={handleRunAICheck}
            disabled={aiLoading}
            className="flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1 rounded-md text-[11px] font-bold text-[#FAFAF8] bg-[#14213D] hover:bg-[#0B1220] border border-[#FCA311] active:scale-95 shadow-md shadow-[#14213D]/10 transition-all"
            style={{ cursor: 'pointer' }}
          >
            <Sparkles className="w-3 h-3 text-[#FCA311]" />
            <span>{aiLoading ? 'Analyzing...' : 'Deep AI Check (2 Credits)'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-950/20 border border-red-500/20 text-red-400 text-xs flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 text-red-500" />
          {error}
        </div>
      )}

      {/* Grid Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
        
        {/* Left Column: Overall Score & Health Summary */}
        <div className="lg:col-span-5 space-y-6">
          <ScoreRing 
            score={report?.overall_score ?? 0} 
            potentialScore={report?.potential_score ?? 0}
            grade={report?.grade ?? 'C'} 
          />

          {/* Resume Health Report Card */}
          {report?.health_report && (
            <div className="backdrop-blur-md rounded-2xl p-6 space-y-4 relative z-10" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Resume Health Card</h3>
                <span className="text-[11px] font-black text-sky-400 bg-sky-400/10 px-2 py-0.5 rounded border border-sky-500/20">Grade: {report.health_report.overall_grade}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'structure', label: 'Structure' },
                  { key: 'readability', label: 'Readability' },
                  { key: 'ats_compatibility', label: 'ATS Compatibility' },
                  { key: 'keyword_coverage', label: 'Keyword Coverage' },
                  { key: 'recruiter_readability', label: 'Recruiter Assessment' },
                  { key: 'impact', label: 'Impact / Verbs' },
                  { key: 'grammar', label: 'Grammar & Typos' },
                  { key: 'metrics', label: 'STAR Metrics' },
                  { key: 'technical_depth', label: 'Technical Depth' },
                  { key: 'leadership', label: 'Leadership Signals' },
                ].map((item) => {
                  const rawVal = report.health_report?.[item.key];
                  const val = typeof rawVal === 'number' && !isNaN(rawVal) ? rawVal : 0;
                  const scoreColor = val >= 80 ? 'text-emerald-400' : val >= 60 ? 'text-amber-400' : 'text-red-400';
                  const barColor = val >= 80 ? 'bg-emerald-500' : val >= 60 ? 'bg-amber-500' : 'bg-red-500';
                  return (
                    <div key={item.key} className="space-y-1">
                      <div className="flex justify-between text-[11px] font-semibold text-slate-300">
                        <span>{item.label}</span>
                        <span className={scoreColor}>{val}%</span>
                      </div>
                      <div className="h-1 w-full bg-zinc-800/40 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full`} style={{ width: `${val}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Progression History */}
          <div className="backdrop-blur-md rounded-2xl p-6 space-y-4" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>Score Progress</h3>
              <Clock className="w-4 h-4 text-slate-500" />
            </div>
            {history.length >= 2 && (
              <div className="py-2" style={{ borderBottom: '1px solid var(--divider)' }}>
                <HistorySparkline history={history} />
              </div>
            )}
            <div className="space-y-3">
              {history.length === 0 ? (
                <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                  <span>First check completed: <strong style={{ color: 'var(--text-primary)' }}>{(report?.overall_score ?? 0)}</strong></span>
                </div>
              ) : (
                history.slice(0, 4).map((hist, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs pb-2 last:border-0 last:pb-0" style={{ borderBottom: '1px solid var(--divider)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {new Date(hist.updated_at || hist.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <span className="font-black" style={{ color: 'var(--text-primary)' }}>{hist.score || hist.overall_score}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Prioritized Improvements */}
        <div className="lg:col-span-7 space-y-6">
          <div className="backdrop-blur-md rounded-2xl p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
            <h3 className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-secondary)' }}>
              Top Improvements ({topImprovements.length})
            </h3>
            {topImprovements.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto animate-bounce" />
                <h4 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>All Key Benchmarks Met!</h4>
                <p className="text-xs max-w-sm mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  No priority issues found. Your resume meets core formatting and content metrics.
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[640px] overflow-y-auto pr-2">
                {topImprovements.map(issue => (
                  <ImprovementCard 
                    key={issue.id} 
                    issue={issue} 
                    onEditManually={handleEditManually}
                    onFixWithAI={handleFixWithAI}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Other Compliance Findings */}
          {otherIssues.length > 0 && (
            <div className="backdrop-blur-md rounded-2xl p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
              <h3 className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-secondary)' }}>
                Other Detailed Checked Findings ({otherIssues.length})
              </h3>
              <div className="space-y-4">
                {otherIssues.map(issue => (
                  <div key={issue.id} className="p-4 rounded-xl flex justify-between items-center text-xs" style={{ background: 'var(--surface-1)', border: '1px solid var(--card-border)' }}>
                    <div>
                      <span className="font-bold block" style={{ color: 'var(--text-primary)' }}>{issue.title}</span>
                      <span className="mt-0.5 block text-2xs" style={{ color: 'var(--text-muted)' }}>{issue.description}</span>
                    </div>
                    <button 
                      onClick={() => handleEditManually(issue)}
                      className="px-2.5 py-1 rounded text-2xs font-bold active:scale-95"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                    >
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Category Score Details (Quality Meter Breakdown) - Full-Width at Bottom */}
      <div className="backdrop-blur-md rounded-2xl p-6 space-y-6 relative z-10" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-primary)' }}>Category Score Details</h3>
          <p className="text-[11px] mt-1" style={{ color: 'var(--text-secondary)' }}>Granular checks mapped to industry standards.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {['structure', 'content', 'impact', 'formatting', 'keywords'].map((cat) => {
            const catData = report?.categories?.[cat] || { score: 0, rules_checked: 0, rules_passed: 0, checks: [] };
            const barColor = cat === 'structure' ? 'bg-blue-500' : cat === 'content' ? 'bg-cyan-500' : cat === 'impact' ? 'bg-orange-500' : cat === 'formatting' ? 'bg-emerald-500' : 'bg-sky-500';
            return (
              <div key={cat} className="p-5 rounded-xl space-y-3" style={{ background: 'var(--surface-1)', border: '1px solid var(--card-border)' }}>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold capitalize" style={{ color: 'var(--text-primary)' }}>{cat}</span>
                  <span className="font-bold" style={{ color: 'var(--brand)' }}>{catData.score}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                  <div className={`h-full ${barColor} rounded-full transition-all duration-1000`} style={{ width: `${catData.score}%` }} />
                </div>
                {catData.checks && catData.checks.length > 0 && (
                  <div className="space-y-1.5 pt-2 mt-2" style={{ borderTop: '1px solid var(--divider)' }}>
                    {catData.checks.map((check) => {
                      const isSkipped = check.status === 'SKIPPED';
                      const icon = isSkipped ? (
                        <span className="w-3.5 h-3.5 font-mono text-[9px] shrink-0 flex items-center justify-center rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--card-border)', color: 'var(--text-muted)' }}>—</span>
                      ) : check.passed ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      ) : (
                        <span className="text-red-500 font-bold shrink-0 w-3.5 h-3.5 flex items-center justify-center">×</span>
                      );
                      return (
                        <div key={check.rule_id} className="flex items-center justify-between text-[11px]">
                          <span className="flex items-center gap-1.5" style={{ color: isSkipped ? 'var(--text-muted)' : 'var(--text-secondary)' }}>
                            {icon}
                            {check.name}
                            {isSkipped && <span className="text-[9px] px-1 rounded scale-90" style={{ color: 'var(--text-muted)', background: 'var(--surface-1)', border: '1px solid var(--card-border)' }}>skipped</span>}
                          </span>
                          <span className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>{check.rule_id}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
